import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import { Image, SVGSprite } from "@/components/Atoms";
import FormInputs from "@/components/Organisms/HookFormInputs/V1";
import Typography from "@/foundation/Typography/V2";

import BookSlotFormWrapper from "../../BookSlotFormWrapper";
import useCallbackForm from "./useCallbackForm";

import { classNameGenerator } from "@/utils";

import { useAppSelector } from "@/hooks";

import styles from "./CallbackForm.module.scss";

interface CallbackFormProps {
  showModal?: boolean;
  setShowModal?: (showModal: boolean) => void;
}

const CallbackForm: React.FC<CallbackFormProps> = ({
  showModal,
  setShowModal = () => {},
}) => {
  const cls = classNameGenerator(styles);

  const { data } = useAppSelector((state) => state.bootCamp);

  const { imageUrl, label, prefix, background } = data?.formSection || {};

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isFormSubmitted) {
      timer = setTimeout(() => {
        setIsFormSubmitted(false);
        setShowModal(false);
      }, 5000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isFormSubmitted, setShowModal]);

  const { methods, isLoading, onBlur, requestCallbackHandler } =
    useCallbackForm({
      setIsFormSubmitted,
    });

  const components = [
    {
      name: "name",
      props: {
        name: "name",
        className: cls("name"),
      },
    },
    {
      name: "email",
      props: {
        name: "email",
        className: cls("email"),
        onBlur,
      },
    },
    {
      name: "phone",
      props: {
        name: "mobile",
        className: cls("phone"),
        onBlur,
      },
    },
    {
      props: {
        children: (
          <Typography
            variant="paragraph1"
            className={cls("optionalText")}
            dangerouslySetInnerHTML={{ __html: "OPTIONAL" }}
          />
        ),
      },
    },
    {
      name: "name",
      props: {
        name: "referrerName",
        className: cls("name"),
        placeholder: "Name of referrer",
      },
    },
    {
      name: "phone",
      props: {
        name: "referrerMobile",
        className: cls("phone"),
        placeholder: "Phone number of referrer",
      },
    },
    {
      name: "submit",
      props: {
        children: (
          <Typography
            variant="span"
            dangerouslySetInnerHTML={{
              __html: "Request a call back",
            }}
          />
        ),
        className: cls("submitButton"),
        isLoading,
      },
    },
    {
      props: {
        children: (
          <Typography
            variant="paragraph1"
            className={cls("infoText")}
            dangerouslySetInnerHTML={{
              __html:
                "Our team will call you within 24 hours to help you get started",
            }}
          />
        ),
      },
    },
  ];

  const form = (
    <FormProvider {...methods}>
      <form className={cls("formWrapper")} onSubmit={requestCallbackHandler}>
        <FormInputs components={components} />
      </form>
    </FormProvider>
  );

  return isFormSubmitted ? (
    <div className={cls("callbackSuccess")}>
      <SVGSprite icon="icon-payment-success" className={cls("icon")} />
      <Typography variant="heading4" className={cls("title")}>
        {"Details submitted"}
      </Typography>
      <Typography variant="paragraph1" className={cls("description")}>
        {"You'll get a call back in 24 hours!"}
      </Typography>
    </div>
  ) : (
    <div className={cls("callbackForm")}>
      <div className={cls("callbackFormWrapper")}>
        {showModal ? (
          <BookSlotFormWrapper
            title="Submit details to book a slot"
            subtitle="Our team will call you within 24 hours to help you get started"
          >
            {form}
          </BookSlotFormWrapper>
        ) : (
          <>
            <div style={{ background }} className={cls("imageContainer")}>
              <Image src={imageUrl} alt="yoga image" className={cls("image")} />
              <div className={cls("heading")}>
                <Typography
                  variant="paragraph1"
                  className={cls("prefix")}
                  dangerouslySetInnerHTML={{ __html: prefix }}
                />
                <Typography
                  variant="paragraph1"
                  className={cls("label")}
                  dangerouslySetInnerHTML={{ __html: label }}
                />
              </div>
            </div>
            {form}
          </>
        )}
      </div>
    </div>
  );
};

export default CallbackForm;
